import { useCallback } from 'react';
import { Link } from 'react-router-dom';
import { User, Package, LogOut, Building2, Phone } from 'lucide-react';
import { useAuthContext } from '@flote/app-core';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@flote/ui';
import { cn } from '@flote/ui';

export function UserDropdown() {
  const { customer, logout, isLoading: authLoading } = useAuthContext();

  const handleSignOut = useCallback(async () => {
    await logout();
  }, [logout]);

  if (!customer) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={cn(
            'relative p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors',
            'focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0'
          )}
          aria-label="User menu"
        >
          <User className="w-5 h-5" />
          {/* Authenticated indicator */}
          <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full ring-2 ring-white" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-72 focus:outline-none focus-visible:outline-none">
        {/* Customer Info Section */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-2">
            <p className="text-sm font-medium leading-none truncate">
              {customer.email}
            </p>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Building2 className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{customer.companyName}</span>
            </div>
            {customer.name && (
              <p className="text-xs text-muted-foreground truncate">
                {customer.name}
              </p>
            )}
            {customer.phone && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Phone className="w-3 h-3 flex-shrink-0" />
                <span>{customer.phone}</span>
              </div>
            )}
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator />

        {/* Navigation Actions */}
        <DropdownMenuItem asChild>
          <Link to="/orders" className="flex items-center gap-2 cursor-pointer">
            <Package className="w-4 h-4" />
            <span>Order History</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Sign Out */}
        <DropdownMenuItem
          onClick={handleSignOut}
          disabled={authLoading}
          className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
        >
          <LogOut className="w-4 h-4 mr-2" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
